import {
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { type QueryKey } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useInvalidateQueries } from '@/shared/hooks';
import { parseUrlState, type GridColumnWidthModel } from './gridState';
import { type GridQueryInput, type GridQueryResult, mapGridFilters } from './gridUtils';
import { useGridCallbacks } from './useGridCallbacks';
import { useGridInitialState } from './useGridInitialState';
import { useGridQueryInput } from './useGridQueryInput';
import { useGridQueryKey } from './useGridQueryKey';
import { useGridQueryLifecycle } from './useGridQueryLifecycle';
import { useGridScrollRetention } from './useGridScrollRetention';
import { useGridSortState } from './useGridSortState';
import { useGridUrlSync } from './useGridUrlSync';

type UseServerDataGridStateParams<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  columns: ReadonlyArray<GridColDef<T>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  restoreFilterModel?: boolean;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  isAuthenticated?: boolean;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  active?: boolean;
  skeletonGetRowHeight?: DataGridProps['getRowHeight'] | null;
  getRowHeight?: DataGridProps['getRowHeight'];
  preservePreviousData?: boolean;
};

export type UseServerDataGridStateResult<T extends GridValidRowModel> = {
  errorMessage: string | null;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  showFetchSkeleton: boolean;
  showPaginationSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
  resolvedPageSizeOptions: number[];
  resolvedInitialPageSize: number;
  resolvedHeight: number | undefined;
  resolvedNoRowsText: string;
  resolvedSkeletonRowHeight: DataGridProps['getRowHeight'] | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  handleSortModelChange: (model: GridSortModel) => void;
  captureScrollAnchor: () => void;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  handleFilterModelChange: (model: GridFilterModel) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  columnWidthModel: GridColumnWidthModel;
  handleColumnWidthChange: NonNullable<DataGridProps['onColumnWidthChange']>;
};
const DEFAULT_VIEWPORT_HEIGHT = 780;

const isColumnVisibilityModelEqual = (
  left: GridColumnVisibilityModel,
  right: GridColumnVisibilityModel,
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
};

const useResolvedHeight = (autoHeight: boolean, height?: number): number | undefined => {
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_VIEWPORT_HEIGHT;
    return window.innerHeight;
  });
  useEffect(() => {
    if (autoHeight) return;
    if (typeof height === 'number') return;
    if (typeof window === 'undefined') return;
    const handleResize = (): void => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoHeight, height]);

  return useMemo(() => {
    if (autoHeight) return undefined;
    if (typeof height === 'number') return height;
    const buffer = 260;
    return Math.max(420, viewportHeight - buffer);
  }, [autoHeight, height, viewportHeight]);
};

export const useServerDataGridState = <T extends GridValidRowModel>({
  queryPath,
  queryFn,
  initialData,
  columns,
  pageSizeOptions,
  initialPageSize,
  height,
  noRowsText = getEmptyStateMessage('data.noResultsFound'),
  filteredNoRowsText,
  onRowsChange,
  onQueryMeta,
  onDataUpdatedAt,
  fixedFilters,
  search,
  initialSortModel: initialSortModelProp,
  initialFilterModel: initialFilterModelProp,
  queryKeyPrefix,
  queryKey: providedQueryKey,
  restoreFilterModel = true,
  onRefetch,
  onInvalidate,
  onQueryInput,
  isAuthenticated = true,
  organisationId,
  projectId,
  syncStateToUrl = false,
  autoHeight = false,
  columnVisibilityModel: columnVisibilityModelProp,
  columnVisibilityPresets,
  columnVisibilityPreset,
  refetchIntervalMs,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  active = true,
  skeletonGetRowHeight,
  getRowHeight,
  preservePreviousData = false,
}: UseServerDataGridStateParams<T>): UseServerDataGridStateResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invalidateQueries = useInvalidateQueries();
  const urlState = useMemo(
    () => parseUrlState(searchParams, syncStateToUrl),
    [searchParams, syncStateToUrl],
  );

  const resolvedHeight = useResolvedHeight(autoHeight, height);

  const {
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    initialPaginationModel,
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    initialColumnWidthModel,
  } = useGridInitialState({
    columns,
    pageSizeOptions,
    initialPageSize,
    urlState,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    restoreFilterModel,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
  });

  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initialPaginationModel);
  const { sortModel, handleSortModelChange: setSortModelInternal } =
    useGridSortState(initialSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );
  const [columnWidthModel, setColumnWidthModel] =
    useState<GridColumnWidthModel>(initialColumnWidthModel);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const toErrorMessage = useApiErrorMessage();
  useEffect(() => {
    if (columnVisibilityModelProp === undefined) return;
    setColumnVisibilityModel((previousModel) =>
      isColumnVisibilityModelEqual(previousModel, columnVisibilityModelProp)
        ? previousModel
        : columnVisibilityModelProp,
    );
  }, [columnVisibilityModelProp]);

  const handleColumnWidthChange = useCallback<NonNullable<DataGridProps['onColumnWidthChange']>>(
    (params) => {
      const width = params.width;
      const field = params.colDef.field;
      if (typeof width !== 'number') return;
      setColumnWidthModel((prev) => {
        if (prev[field] === width) return prev;
        return { ...prev, [field]: width };
      });
    },
    [],
  );

  useEffect(() => {
    if (!resolvedPageSizeOptions.includes(paginationModel.pageSize)) {
      setPaginationModel((prev) => ({ ...prev, page: 0, pageSize: resolvedInitialPageSize }));
    }
  }, [paginationModel.pageSize, resolvedInitialPageSize, resolvedPageSizeOptions]);

  const { queryInput, queryInputKey, fixedFiltersKey } = useGridQueryInput({
    paginationModel,
    sortModel,
    filterModel,
    fixedFilters,
    search,
  });
  const finalQueryKey = useGridQueryKey({
    queryPath,
    queryKeyPrefix,
    providedQueryKey,
    organisationId,
    projectId,
    queryInputKey,
  });
  const [pendingPaginationModel, setPendingPaginationModel] = useState<GridPaginationModel | null>(
    null,
  );
  const [pendingRowTransition, setPendingRowTransition] = useState(false);

  const {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
  } = useGridQueryLifecycle<T>({
    finalQueryKey,
    isAuthenticated,
    queryFn,
    initialData,
    queryInput,
    preservePreviousData:
      preservePreviousData || pendingPaginationModel !== null || pendingRowTransition,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchIntervalMs,
    active,
  });

  useEffect(() => {
    if (!active) return;
    if (queryIsFetching) return;
    const lastPage = Math.max(0, Math.ceil(rowCount / Math.max(1, paginationModel.pageSize)) - 1);
    if (paginationModel.page <= lastPage) return;
    const nextModel = { ...paginationModel, page: lastPage };
    setPendingPaginationModel(nextModel);
    setPaginationModel(nextModel);
  }, [active, paginationModel.page, paginationModel.pageSize, queryIsFetching, rowCount]);

  const searchKey = useMemo(() => search ?? '', [search]);
  const sortModelKey = useMemo(() => JSON.stringify(sortModel), [sortModel]);

  const {
    handlePaginationModelChange: handlePaginationModelChangeWithScrollRetention,
    handleSortModelChange: handleSortModelChangeWithScrollRetention,
    captureScrollAnchor,
  } = useGridScrollRetention({
    setPaginationModel,
    setSortModelInternal,
    paginationPage: paginationModel.page,
    queryIsFetching,
    queryDataUpdatedAt: query.dataUpdatedAt,
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const paginationChanged =
        model.page !== paginationModel.page || model.pageSize !== paginationModel.pageSize;
      setPendingPaginationModel(paginationChanged ? model : null);
      setPendingRowTransition(paginationChanged);
      handlePaginationModelChangeWithScrollRetention(model);
    },
    [
      handlePaginationModelChangeWithScrollRetention,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const sortChanged = JSON.stringify(model) !== sortModelKey;
      if (sortChanged) {
        setPendingRowTransition(true);
      }
      handleSortModelChangeWithScrollRetention(model);
    },
    [handleSortModelChangeWithScrollRetention, sortModelKey],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      if (!active) return;
      setPendingRowTransition(true);
      captureScrollAnchor();
      setFilterModel(model);
    },
    [active, captureScrollAnchor],
  );

  const lastSortModelKey = useRef(sortModelKey);
  useEffect(() => {
    if (!active) return;
    if (lastSortModelKey.current === sortModelKey) return;
    lastSortModelKey.current = sortModelKey;
    setPendingRowTransition(true);
    if (paginationModel.page === 0) return;
    captureScrollAnchor();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [active, captureScrollAnchor, paginationModel.page, sortModelKey]);

  const lastFixedFiltersKey = useRef(fixedFiltersKey);
  useEffect(() => {
    if (!active) return;
    if (lastFixedFiltersKey.current === fixedFiltersKey) return;
    lastFixedFiltersKey.current = fixedFiltersKey;
    setPendingRowTransition(true);
    if (paginationModel.page === 0) return;
    captureScrollAnchor();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [active, captureScrollAnchor, fixedFiltersKey, paginationModel.page]);

  const lastSearchKey = useRef(searchKey);
  useEffect(() => {
    if (!active) return;
    if (lastSearchKey.current === searchKey) return;
    lastSearchKey.current = searchKey;
    setPendingRowTransition(true);
    if (paginationModel.page === 0) return;
    captureScrollAnchor();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [active, captureScrollAnchor, paginationModel.page, searchKey]);

  useGridCallbacks<T>({
    onQueryInput: active ? onQueryInput : undefined,
    queryInput,
    queryInputKey,
    onRowsChange: active ? onRowsChange : undefined,
    rowsData,
    onQueryMeta: active ? onQueryMeta : undefined,
    queryData: query.data,
    onDataUpdatedAt: active ? onDataUpdatedAt : undefined,
    lastUpdatedAt,
    onRefetch: active ? onRefetch : undefined,
    onInvalidate: active ? onInvalidate : undefined,
    invalidateQueries,
    finalQueryKey,
    queryRefetch: query.refetch,
  });

  useEffect(() => {
    if (!active) return;
    if (query.status === 'error') {
      setErrorMessage(toErrorMessage(query.error));
    }
    if (query.status === 'success') {
      setErrorMessage(null);
    }
  }, [active, query.error, query.status, toErrorMessage]);

  useGridUrlSync({
    syncStateToUrl: active && syncStateToUrl,
    searchParams,
    setSearchParams,
    paginationModel,
    sortModel,
    filterModel,
  });

  const hasActiveFilters = mapGridFilters(filterModel).length > 0;
  const resolvedNoRowsText = hasActiveFilters
    ? (filteredNoRowsText ?? 'No results match your filters')
    : noRowsText;
  const resolvedSkeletonRowHeight =
    skeletonGetRowHeight === null ? undefined : (skeletonGetRowHeight ?? getRowHeight);
  useEffect(() => {
    if (pendingPaginationModel === null) return;
    if (queryIsFetching) return;
    setPendingPaginationModel(null);
  }, [pendingPaginationModel, paginationModel, queryIsFetching]);
  useEffect(() => {
    if (!pendingRowTransition) return;
    if (queryIsFetching) return;
    setPendingRowTransition(false);
  }, [pendingRowTransition, queryIsFetching]);
  const showPaginationSkeleton = pendingPaginationModel !== null || pendingRowTransition;

  return {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    showPaginationSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    setPaginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    setFilterModel,
    handleFilterModelChange,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnWidthModel,
    handleColumnWidthChange,
  };
};
